import type * as MapLibreGL from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
	createContext,
	forwardRef,
	type ReactNode,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

type MapContextValue = {
	map: MapLibreGL.Map | null;
	isLoaded: boolean;
};

const MapContext = createContext<MapContextValue | null>(null);

function useMap() {
	const context = useContext(MapContext);
	if (!context) throw new Error("useMap must be used within a Map component");
	return context;
}

export type MapRef = {
	flyTo: (options: MapLibreGL.FlyToOptions) => void;
	fitBounds: (
		bounds: MapLibreGL.LngLatBoundsLike,
		options?: MapLibreGL.FitBoundsOptions,
	) => void;
};

type MapProps = {
	className?: string;
	children?: ReactNode;
	center: [number, number];
	zoom: number;
	scrollZoom?: boolean;
	interactive?: boolean;
	initialView?: {
		bounds: MapLibreGL.LngLatBoundsLike;
		padding?: number;
	};
	styles?: {
		light?: string;
	};
};

const defaultStyles = {
	light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
};

const MapView = forwardRef<MapRef, MapProps>(function MapView(
	{
		className,
		children,
		center,
		zoom,
		scrollZoom = false,
		interactive = true,
		initialView,
		styles,
	},
	ref,
) {
	const containerRef = useRef<HTMLDivElement>(null);
	const mapInstanceRef = useRef<MapLibreGL.Map | null>(null);
	const [map, setMap] = useState<MapLibreGL.Map | null>(null);
	const [isLoaded, setIsLoaded] = useState(false);

	const styleUrls = useMemo(
		() => ({
			light: styles?.light ?? defaultStyles.light,
		}),
		[styles?.light],
	);

	useImperativeHandle(
		ref,
		() => ({
			flyTo: (options) => mapInstanceRef.current?.flyTo(options),
			fitBounds: (bounds, options) =>
				mapInstanceRef.current?.fitBounds(bounds, options),
		}),
		[],
	);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const initialStyle = styleUrls.light;

		let mapInstance: MapLibreGL.Map | null = null;
		let isDisposed = false;

		(async () => {
			const module = await import("maplibre-gl");
			if (isDisposed) return;

			mapInstance = new module.Map({
				container,
				style: initialStyle,
				center,
				zoom,
				renderWorldCopies: false,
				attributionControl: { compact: true },
			});

			if (scrollZoom) mapInstance.scrollZoom.enable();
			else mapInstance.scrollZoom.disable();

			if (!interactive) {
				mapInstance.dragPan.disable();
				mapInstance.dragRotate.disable();
				mapInstance.doubleClickZoom.disable();
				mapInstance.keyboard.disable();
				mapInstance.touchZoomRotate.disable();
				mapInstance.boxZoom.disable();
			}

			const onLoad = () => {
				setIsLoaded(true);
				if (initialView) {
					mapInstance?.fitBounds(initialView.bounds, {
						padding: initialView.padding ?? 48,
						duration: 0,
					});
				}
			};

			mapInstance.on("load", onLoad);
			mapInstanceRef.current = mapInstance;
			setMap(mapInstance);

			if (isDisposed) {
				mapInstance.remove();
				mapInstanceRef.current = null;
				setMap(null);
				setIsLoaded(false);
			}
		})();

		return () => {
			isDisposed = true;
			mapInstance?.remove();
			mapInstanceRef.current = null;
			setMap(null);
			setIsLoaded(false);
		};
	}, [center, scrollZoom, styleUrls.light, zoom, interactive, initialView]);

	useEffect(() => {
		if (!map) return;
		map.jumpTo({ center, zoom });
	}, [center, map, zoom]);

	const contextValue = useMemo(() => ({ map, isLoaded }), [isLoaded, map]);

	return (
		<MapContext.Provider value={contextValue}>
			<div ref={containerRef} className={cn("relative w-full", className)}>
				{map ? children : null}
			</div>
		</MapContext.Provider>
	);
});

type MarkerContextValue = {
	marker: MapLibreGL.Marker;
	popupContainer: HTMLDivElement;
};

const MarkerContext = createContext<MarkerContextValue | null>(null);

function useMarkerContext() {
	const context = useContext(MarkerContext);
	if (!context)
		throw new Error("Marker components must be used within MapMarker");
	return context;
}

type MapMarkerProps = {
	longitude: number;
	latitude: number;
	children?: ReactNode;
};

function MapMarker({ longitude, latitude, children }: MapMarkerProps) {
	const { map, isLoaded } = useMap();
	const [marker, setMarker] = useState<MapLibreGL.Marker | null>(null);
	const popupContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!isLoaded || !map) return;

		const element = document.createElement("div");
		const popupContainer = document.createElement("div");
		popupContainerRef.current = popupContainer;

		let markerInstance: MapLibreGL.Marker | null = null;
		let popup: MapLibreGL.Popup | null = null;
		let isDisposed = false;

		(async () => {
			const module = await import("maplibre-gl");
			if (isDisposed) return;

			popup = new module.Popup({ offset: 16, closeButton: true })
				.setMaxWidth("360px")
				.setDOMContent(popupContainer);

			markerInstance = new module.Marker({ element })
				.setLngLat([longitude, latitude])
				.setPopup(popup)
				.addTo(map);

			setMarker(markerInstance);
		})();

		return () => {
			isDisposed = true;
			markerInstance?.remove();
			popup?.remove();
			popupContainerRef.current = null;
			setMarker(null);
		};
	}, [isLoaded, latitude, longitude, map]);

	useEffect(() => {
		marker?.setLngLat([longitude, latitude]);
	}, [latitude, longitude, marker]);

	if (!marker || !popupContainerRef.current) return null;

	return (
		<MarkerContext.Provider
			value={{ marker, popupContainer: popupContainerRef.current }}
		>
			{children}
		</MarkerContext.Provider>
	);
}

type MarkerContentProps = {
	className?: string;
	children: ReactNode;
};

function MarkerContent({ className, children }: MarkerContentProps) {
	const { marker } = useMarkerContext();
	return createPortal(
		<div className={cn("relative", className)}>{children}</div>,
		marker.getElement(),
	);
}

type MarkerPopupProps = {
	className?: string;
	children: ReactNode;
};

function MarkerPopup({ className, children }: MarkerPopupProps) {
	const { popupContainer } = useMarkerContext();
	return createPortal(
		<div
			className={cn(
				"relative rounded-xl border border-black/10 bg-white p-3 pr-12 text-sm text-foreground shadow-sm",
				className,
			)}
		>
			{children}
		</div>,
		popupContainer,
	);
}

export { MapView as Map, MapMarker, MarkerContent, MarkerPopup, useMap };
